import React from "react";

/** Maps the app's aspect-ratio keys to a CSS aspect-ratio utility class. */
const ASPECT_CLASS: Record<string, string> = {
  "1x1": "aspect-square",
  "16x9": "aspect-[16/9]",
  "9x16": "aspect-[9/16]",
  "4x3": "aspect-[4/3]",
  "3x4": "aspect-[3/4]",
};

/** Portrait by default so the placeholder reads as an image while generating. */
export const imageAspectClass = (aspectRatio?: string): string =>
  ASPECT_CLASS[aspectRatio || ""] || "aspect-[3/4]";

interface ImagePlaceholderTileProps {
  /** App aspect-ratio key (e.g. "9x16"); defaults to a portrait shape. */
  aspectRatio?: string;
}

/**
 * Loading placeholder shaped like the image that will appear: an image-shaped tile
 * (portrait by default) with a soft gradient, a sweeping shimmer, a floating framed
 * picture icon, and a subtle "Generating" indicator — shown until the real image
 * streams in and replaces it.
 */
export const ImagePlaceholderTile: React.FC<ImagePlaceholderTileProps> = ({ aspectRatio }) => (
  <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-theme-border shadow-sm">
    <style>{`
      @keyframes imgPhShimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
      @keyframes imgPhFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
      @keyframes imgPhDot { 0%, 100% { opacity: .25; } 50% { opacity: 1; } }
    `}</style>

    <div
      className={`relative w-full overflow-hidden bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 dark:from-theme-surface dark:via-theme-background dark:to-theme-surface ${imageAspectClass(
        aspectRatio
      )}`}
    >
      {/* Sweeping shimmer band */}
      <div
        className="pointer-events-none absolute inset-0 imgph-shimmer"
        style={{ animation: "imgPhShimmer 1.8s ease-in-out infinite" }}
      />
      <style>{`
        .imgph-shimmer { background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.7) 50%, transparent 70%); }
        .dark .imgph-shimmer { background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%); }
      `}</style>

      {/* Centered framed icon + label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 dark:bg-white/10 shadow-sm ring-1 ring-gray-200 dark:ring-white/10 backdrop-blur-sm"
          style={{ animation: "imgPhFloat 2.6s ease-in-out infinite" }}
        >
          <svg className="h-7 w-7 text-[#2f4941] dark:text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>

        <div className="flex items-center gap-1.5 text-xs font-medium tracking-wide text-gray-500 dark:text-theme-fg-muted">
          <span>Generating</span>
          <span className="inline-flex gap-0.5">
            <span className="h-1 w-1 rounded-full bg-[#2f4941] dark:bg-theme-primary" style={{ animation: "imgPhDot 1.2s ease-in-out infinite", animationDelay: "0ms" }} />
            <span className="h-1 w-1 rounded-full bg-[#2f4941] dark:bg-theme-primary" style={{ animation: "imgPhDot 1.2s ease-in-out infinite", animationDelay: "180ms" }} />
            <span className="h-1 w-1 rounded-full bg-[#2f4941] dark:bg-theme-primary" style={{ animation: "imgPhDot 1.2s ease-in-out infinite", animationDelay: "360ms" }} />
          </span>
        </div>
      </div>
    </div>
  </div>
);

export default ImagePlaceholderTile;
